import { createFileRoute } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useReplaceFeedLayoutBlocks } from '@/api/api-mutations'
import { useGetChannelResolvedContent, useGetFeedLayout, useGetMarketingChannel } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { UnsavedChangesBlocker } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'

import { BlockListEditor, isWritableBlockType, type EditableBlock } from './-blocks'
import { FeedPreview, type PreviewItem } from './-preview'

type PreviewMode = 'layout' | 'live'

export const Route = createFileRoute('/_authenticated/_general/marketing/layout/$marketingChannelUuid')({
	component: FeedLayoutEditorPage,
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.Layout.Title()
	}
})

function FeedLayoutEditorPage() {
	const { marketingChannelUuid } = Route.useParams()
	const { L } = useI18n()

	const channelQuery = useGetMarketingChannel({ marketingChannelUuid })
	const layoutQuery = useGetFeedLayout({ marketingChannelUuid }, {})
	const replaceBlocks = useReplaceFeedLayoutBlocks(true, marketingChannelUuid)

	const [blocks, setBlocks] = useState<EditableBlock[]>([])
	const [dirty, setDirty] = useState(false)
	const [previewOpen, setPreviewOpen] = useState(true)
	const [previewMode, setPreviewMode] = useState<PreviewMode>('layout')

	// Mirror `dirty` in a ref so the reset effect can read the latest value without depending on it
	// (depending on `dirty` would re-seed from stale query data right after a save clears the flag).
	const dirtyRef = useRef(false)
	const markDirty = (value: boolean) => {
		dirtyRef.current = value
		setDirty(value)
	}

	// The fully-resolved content customers currently receive (saved layout + active campaigns). Only
	// fetched when the live preview is shown.
	const feedQuery = useGetChannelResolvedContent({ marketingChannelUuid }, {}, { enabled: previewMode === 'live' })

	useEffect(() => {
		const layout = layoutQuery.data
		if (!layout) return
		// Don't clobber in-progress edits when a background refetch changes the query data identity.
		if (dirtyRef.current) return
		setBlocks(layout.blocks.map(b => ({ uuid: b.uuid, kind: b.kind, type: b.type, data: b.data ?? {} })))
		markDirty(false)
	}, [layoutQuery.data])

	const handleSave = async () => {
		try {
			const payload: {
				kind: EditableBlock['kind']
				type?: 'BANNER' | 'LIST' | 'MODAL' | 'SEPARATOR'
				data?: EditableBlock['data']
			}[] = []
			for (const b of blocks) {
				if (b.kind === 'CAMPAIGN_ZONE') {
					payload.push({ kind: b.kind, type: undefined, data: undefined })
					continue
				}
				if (!isWritableBlockType(b.type)) continue
				payload.push({ kind: b.kind, type: b.type, data: b.data ?? {} })
			}
			await replaceBlocks.mutateAsync({
				path: { marketingChannelUuid },
				query: {},
				body: { blocks: payload }
			})
			markDirty(false)
			// Saved layout changes the resolved feed; keep the live preview in sync.
			if (previewMode === 'live') void feedQuery.refetch()
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	if (layoutQuery.isLoading) {
		return (
			<SingleColumnPage>
				<Container className='flex flex-1 items-center justify-center'>
					<Text className='text-ui-fg-subtle'>{L.General.Loading()}</Text>
				</Container>
			</SingleColumnPage>
		)
	}

	return (
		<SingleColumnPage>
			<UnsavedChangesBlocker dirty={dirty} />
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={
						channelQuery.data
							? `${L.Marketing.Layout.Title()} — ${channelQuery.data.name}`
							: L.Marketing.Layout.Title()
					}
					rightSide={
						<IconButton
							tooltipText={L.Marketing.Campaign.Preview.Title()}
							onClick={() => setPreviewOpen(o => !o)}
							variant='transparent'
							className='hidden lg:inline-flex'
						>
							{previewOpen ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
						</IconButton>
					}
				/>
				<div className='flex flex-1 gap-6 overflow-hidden'>
					<div className='flex-1 overflow-y-auto p-4 sm:p-6'>
						<div className='flex flex-col gap-4'>
							<Text className='max-w-2xl text-xs text-ui-fg-muted'>{L.Marketing.Layout.Help()}</Text>
							<BlockListEditor
								blocks={blocks}
								allowCampaignZone
								onChange={next => {
									setBlocks(next)
									markDirty(true)
								}}
							/>
							<div className='flex justify-end'>
								<Button onClick={handleSave} disabled={!dirty || replaceBlocks.isPending}>
									{L.Marketing.Channel.Action.Save()}
								</Button>
							</div>
						</div>
					</div>
					{previewOpen && (
						<div className='hidden w-[360px] shrink-0 overflow-y-auto border-l border-ui-border-base bg-ui-bg-subtle p-4 lg:block'>
							<Text className='mb-3 text-center text-xs font-medium uppercase tracking-wide text-ui-fg-muted'>
								{L.Marketing.Campaign.Preview.Title()}
							</Text>
							<div className='mb-3 flex justify-center'>
								<div className='inline-flex rounded-md border border-ui-border-base bg-ui-bg-base p-0.5'>
									{(['layout', 'live'] as const).map(mode => (
										<button
											key={mode}
											type='button'
											onClick={() => setPreviewMode(mode)}
											className={clx(
												'rounded px-3 py-1 text-xs font-medium transition-fg',
												previewMode === mode
													? 'bg-ui-bg-component text-ui-fg-base shadow-borders-base'
													: 'text-ui-fg-subtle hover:text-ui-fg-base'
											)}
										>
											{mode === 'layout'
												? L.Marketing.Layout.PreviewModeLayout()
												: L.Marketing.Layout.PreviewModeLive()}
										</button>
									))}
								</div>
							</div>
							{previewMode === 'live' ? (
								feedQuery.isLoading ? (
									<Text className='text-center text-xs text-ui-fg-subtle'>{L.General.Loading()}</Text>
								) : (
									<>
										<FeedPreview items={feedQuery.data ?? []} />
										<Text className='mt-2 text-center text-[11px] text-ui-fg-muted'>
											{L.Marketing.Layout.LivePreviewHint()}
										</Text>
									</>
								)
							) : (
								<FeedPreview items={layoutToPreview(blocks)} />
							)}
						</div>
					)}
				</div>
			</Container>
		</SingleColumnPage>
	)
}

function layoutToPreview(blocks: EditableBlock[]): PreviewItem[] {
	return blocks.map(b => ({
		uuid: b.uuid,
		type: b.kind === 'CAMPAIGN_ZONE' ? 'CAMPAIGN_ZONE' : (b.type ?? 'CUSTOM'),
		data: b.data ?? {}
	}))
}
